import { object, string } from 'yup';

import { getOperatorFromRule } from '../utils';

import type { RuleGroup, Schema, Rule, Attributes } from './index';
import type { BaseSchema } from 'yup';

type ValidateAllRulesProps = {
  ruleGroup: RuleGroup;
  schema: Schema;
};

type ValidateRuleProps = {
  rule: Rule;
  schema: Schema;
};

export const validateAllRules = ({ ruleGroup, schema }: ValidateAllRulesProps) =>
  ruleGroup?.rules.every((rule) => validateRule({ rule, schema }));

export const validateRule = ({ rule, schema }: ValidateRuleProps) => {
  const isSecondValueRequired = getOperatorFromRule({ rule, schema })?.requiresSecondValue || false;

  const variants = schema[rule.Condition]?.attributes?.find(
    (attribute: Attributes) => attribute.name === rule.Attribute
  )?.variants;
  const isVariantRequired = (variants && variants?.length > 0) ?? false;

  return isRuleValid({ rule, schema, isSecondValueRequired, isVariantRequired });
};

type IsRuleValidProps = {
  isSecondValueRequired: boolean;
  isVariantRequired: boolean;
  rule: Rule;
  schema: Schema;
};

const isRuleValid = ({ rule, schema, isSecondValueRequired, isVariantRequired }: IsRuleValidProps) => {
  try {
    generateSchemaForQueryBuilder({
      rule,
      schema,
    }).validateSync(rule, {
      context: { isSecondValueRequired, isVariantRequired },
    });
    return true;
  } catch {
    return false;
  }
};

const generateSchemaForQueryBuilder = ({ rule, schema }: ValidateRuleProps) => {
  const getYup: BaseSchema = getOperatorFromRule({ rule, schema })?.yup || string();

  return object({
    Attribute: string().required(),
    Condition: string().required(),
    Operator: string().required(),
    Value: getYup.required(),
    SecondValue: getYup
      .when('$isSecondValueRequired', {
        is: true,
        then: (schema) => schema.required(),
        otherwise: (schema) =>
          schema
            .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
            .optional(),
      })
      .test({
        name: 'ValueLessThanSecondValue',
        test() {
          const { Value, SecondValue } = this.parent;

          if (Value && SecondValue) {
            return Value <= SecondValue;
          }
          return true;
        },
      }),
    Variant: string().when('$isVariantRequired', (isVariantRequired, schema) => {
      if (isVariantRequired) {
        return schema.required().nullable();
      }
      return schema.nullable();
    }),
  });
};
